import { useState } from "react";
import { Info } from "lucide-react";
import { motion } from "motion/react";
import { SalaryData } from "../../../types/SalaryData";
import { MarketData } from "../../../types/MarketData";  
import { Skeleton } from "../../SalaryCheck/components/ui/skeleton";

interface OptimizedResultCardProps {
  salaryData: SalaryData;
  marketData: MarketData | null;
  isCalculating: boolean;
}

const situationLabels: Record<number, string> = {
    1: "lønforhandling så din løn er fair.",
    2: "lønforhandling.",
    3: "lønforhandling som nyuddannet.",
    4: "lønforhandling når du skal have nyt job.",
  };

const getSituationLabel = (value: unknown): string => {
  if (typeof value === "number") {
    return situationLabels[value] ?? "lønforhandling.";
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return situationLabels[numeric] ?? "lønforhandling.";
    }
  }
  return "lønforhandling.";
};

function getVerdict(diffPercent: number, isClose: boolean) {
  if (isClose)
    return {
      label: "Midt i markedet",
      description: "Din nuværende løn ligger inden for det typiske marked",
      color: "#008e9f",
      position: 50,
    };
  if (diffPercent > 10)
    return {
      label: "Højt i markedet",
      description: "Din nuværende løn ligger i den høje ende af markedet",
      color: "#059669",
      position: 85,
    };
  if (diffPercent > 0)
    return {
      label: "Over markedet",
      description: "Din nuværende løn ligger lidt over det typiske marked",
      color: "#10b981",
      position: 65,
    };
  if (diffPercent > -10)
    return {
      label: "Under markedet",
      description: "Din nuværende løn ligger lidt under det typiske marked",
      color: "#f59e0b",
      position: 35,
    };
  return {
    label: "Lavt i markedet",
    description: "Din nuværende løn ligger i den lave ende af markedet",
    color: "#ef4444",
    position: 15,
  };
}

function getDrivers(salaryData: SalaryData) {
  const drivers: string[] = [];
  if (salaryData.experience !== null) {
    if (salaryData.experience >= 10) drivers.push("Din erfaring trækker lønnen op");
    else if (salaryData.experience <= 2) drivers.push("Begrænset erfaring påvirker lønnen");
  }
  if (salaryData.education === "Lang") drivers.push("Lang uddannelse giver lønpræmie");
  if (salaryData.education === "Grundskole") drivers.push("Uddannelsesniveau påvirker lønnen");
  if (salaryData.region === "Hovedstaden") drivers.push("Hovedstaden har højere lønniveau");
  if (salaryData.region === "Nordjylland") drivers.push("Nordjylland har lavere lønniveau");
  if (drivers.length === 0) drivers.push("Jobtitel og erfaring er de største lønfaktorer");
  return drivers.slice(0, 3);
}

function getSituationVerdict(situationId: number | undefined, diffPercent: number) {
  const relation = Math.abs(diffPercent) <= 3 ? "average" : diffPercent > 0 ? "over" : "under";

  const verdicts: Record<number, Record<string, string>> = {
    1: { // Fair løn
      over: "Det kan give dig indsigt i, hvad der typisk skal til for at fastholde dit niveau.",
      average: "Det kan hjælpe dig med at vurdere, hvornår det giver mening at gøre noget aktivt ved din løn.",
      under: "Det kan give dig overblik over, hvilke greb der kan rykke din løn.",
    },
    2: { // Lønforhandling
      over: "Det kan give dig indsigt i, hvad der typisk skal til for at fastholde dit niveau.",
      average: "Det kan hjælpe dig med at vurdere, hvornår det giver mening at gøre noget aktivt ved din løn.",
      under: "Det kan give dig overblik over, hvilke greb der kan rykke din løn.",
    },
    3: { // Nyuddannet
      over: "Det kan give dig indsigt i, hvad der typisk skal til for at fastholde dit niveau.",
      average: "Det kan hjælpe dig med at vurdere, hvornår det giver mening at gøre noget aktivt ved din løn.",
      under: "Det kan give dig overblik over, hvilke greb der kan rykke din løn.",
    },
    4: { // Søger nyt job 
      over: "Det kan give dig indsigt i, hvad der typisk skal til for at fastholde dit niveau.",
      average: "Det kan hjælpe dig med at vurdere, hvornår det giver mening at gøre noget aktivt ved din løn.",
      under: "Det kan give dig overblik over, hvilke greb der kan rykke din løn.",
    },
  };

  return (situationId && verdicts[situationId]?.[relation]) ??
    (relation === "over"
      ? "Din løn ligger over markedsgennemsnittet."
      : relation === "average"
      ? "Din løn ligger omkring markedsgennemsnittet."
      : "Din løn ligger under markedsgennemsnittet.");
}

function FairnessBarometer({ verdict, salaryData, marketData }: { verdict: ReturnType<typeof getVerdict>; marketData: MarketData; salaryData: SalaryData }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isVeryLowData = marketData.sampleSize < 5;

  return (
    <div className="result-card">
      {/* Header with status badge */}
      <div className="result-card__header">
        <h3 className="result-card__title">Sådan er din løn</h3>
        <div
          className="result-card__status-badge"
          style={{
            backgroundColor: verdict.color === '#ef4444' ? '#fef2f2' :
                           verdict.color === '#f59e0b' ? '#fffbeb' :
                           verdict.color === '#008e9f' ? '#e6f4f5' :
                           verdict.color === '#10b981' ? '#f0fdf4' : '#f0fdf4',
            color: verdict.color
          }}
        >
          {verdict.label}
        </div>
      </div>

      {/* 5-level fairness bar */}
      <div className="result-card__bar-section">
        <div className="result-card__bar-track">
          {/* Position marker - white bar */}
          <motion.div className="result-card__position-marker" style={{ left: `${verdict.position}%` }} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}>
            <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20L1 1H23L12 20Z" fill="white" stroke="#9ca3af" strokeWidth="2" />
            </svg>
          </motion.div>
        </div>

        {/* Scale labels */}
        <div className="result-card__scale">
          <span className="result-card__scale-label">Lavt</span>
          <span className="result-card__scale-label">Under</span>
          <span className="result-card__scale-label">Midt</span>
          <span className="result-card__scale-label">Over</span>
          <span className="result-card__scale-label">Højt</span>
        </div>
      </div>

      {/* Current salary */}
      <motion.div className="result-card__section" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
        <div className="result-card__row">
          <span className="result-card__label">Din nuværende bruttoløn</span>
          <span className="result-card__value">{salaryData.salary.toLocaleString("da-DK")} kr.</span>
        </div>
      </motion.div>

      {/* Market data */}
      <div className="result-card__market-details">
        <div className="result-card__row">
          <span className="result-card__label">Markedsgennemsnit</span>
          <span className="result-card__value">{marketData.average.toLocaleString("da-DK")} kr.</span>
        </div>
        <div className="result-card__row">
          <span className="result-card__label">Typisk løninterval</span>
          <span className="result-card__value">
            {marketData.rangeMin.toLocaleString("da-DK")}–{marketData.rangeMax.toLocaleString("da-DK")} kr.
          </span>
        </div>
      </div>

      {/* Divider before datagrundlag */}
      <div className="result-card__divider" />

      {/* Datagrundlag */}
      <div className="result-card__row">
        <span className="result-card__datasource-text">Datagrundlag</span>
        {isVeryLowData ? (
          <div className="result-card__datasource-status">
            <span className="result-card__datasource-text">Begrænset</span>
            <div className="result-card__tooltip-trigger" onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)}>
              <Info className="result-card__tooltip-icon" />
              {showTooltip && (
                <div className="result-card__tooltip">
                  <div className="result-card__tooltip-arrow" />
                  Vi har fundet færre relevante profiler end normalt for denne beregning. Derfor er resultatet vejledende og baseret på et bredere sammenligningsgrundlag.
                </div>
              )}
            </div>
          </div>
        ) : (
          <span className="result-card__datasource-text">ca. {marketData.sampleSize} profiler</span>
        )}
      </div>
    </div>
  );
}

export function ResultCard({ salaryData, marketData, isCalculating }: OptimizedResultCardProps) {
  const hasData = salaryData.jobTitle && salaryData.salary > 0;
  if (!hasData) return null;

  if (isCalculating) {
    return (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-gray-100 p-6">
            <Skeleton className="h-8 w-32 mb-4" />
            <Skeleton className="h-12 w-full rounded-full mb-4" />
            <Skeleton className="h-6 w-40 mb-2" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
    );
  }

  if (!marketData) return null;

  const diff = salaryData.salary - marketData.average;
  const diffPercent = Math.round((diff / marketData.average) * 100);
  const isClose = Math.abs(diffPercent) <= 3;
  const graphResult = marketData.graphResult ?? [];
  const maxGraphValue = graphResult.length ? Math.max(...graphResult.map((item) => item.yAxis)) : 1;

  const verdict = getVerdict(diffPercent, isClose);
  const drivers = getDrivers(salaryData);
  const situationVerdictText = getSituationVerdict(salaryData.situationId, diffPercent);
  const isLowData = marketData.sampleSize < 10;

  const situationLabel = getSituationLabel(salaryData.situationId);

  return (
    <>
      <div className="result-card__container">
        <div className="result-card__intro">
          <h2 className="">Hej {salaryData.name}</h2>
          <p className="result-card__intro-text">Som {salaryData.jobTitle} {salaryData.experience && `med ${salaryData.experience} års erfaring `}ligger din løn {verdict.label.toLowerCase()}. 
            Men løn er mere end tal. Her på siden finder du dit resultat, hvad du også skal være opmærksom på i din lønpakke samt tips og tricks til {situationLabel}</p>
          {/* <p className="result-card__intro-text">{situationVerdictText}</p> */}
        </div>
        <div className="result-card__column">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }} className="result-card__vertical-stack">
            <FairnessBarometer verdict={verdict} salaryData={salaryData} marketData={marketData} />
          </motion.div>
        </div>
        <div className="result-card__column">
          <motion.div key="optimized-result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }} className="result-card__vertical-stack">
            <div className="result-card__summary">
              <div className="result-card__summary-header">
                <h3 className="result-card__summary-title">Din løn i kontekst</h3>
              </div>

              <div className="result-card__summary-grid">
                <div className="result-card__summary-item">
                  <div className="result-card__summary-item-label">Jobtitel</div>
                  <div className="result-card__summary-item-value">{salaryData.jobTitle}</div>
                </div>
                {salaryData.responsibility && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Ledererfaring</div>
                    <div className="result-card__summary-item-value">{salaryData.responsibility} år</div>
                  </div>
                )}
                {salaryData.education && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Uddannelse</div>
                    <div className="result-card__summary-item-value">{salaryData.education} år</div>
                  </div>
                )}
                {salaryData.region && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Region</div>
                    <div className="result-card__summary-item-value">{salaryData.region}</div>
                  </div>
                )}
                {salaryData.experience !== null && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Erfaring</div>
                    <div className="result-card__summary-item-value">{salaryData.experience} år</div>
                  </div>
                )}
                {salaryData.situation && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Situation</div>
                    <div className="result-card__summary-item-value">{salaryData.situation}</div>
                  </div>
                )}
                {salaryData.age && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Aldersgruppe</div>
                    <div className="result-card__summary-item-value">{salaryData.age}</div>
                  </div>
                )}
                {salaryData.sector && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Sektor</div>
                    <div className="result-card__summary-item-value">{salaryData.sector}</div>
                  </div>
                )}
                {salaryData.created && (
                  <div className="result-card__summary-item">
                    <div className="result-card__summary-item-label">Dato for løntjek</div>
                    <div className="result-card__summary-item-value">{salaryData.created.toLocaleDateString()}</div>
                  </div>
                )}
              </div>

              {isLowData && <div className="result-card__low-data-warning">Datagrundlaget er lavt for denne beregning, så resultatet skal ses som vejledende.</div>}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
